import { useCallback, useEffect, useRef, useState } from "react";
import type { Structure } from "model/OrganizationalStructure";
import { Api } from "services/api/api.service";
import { err, ok } from "utils/result";
import type { Result } from "utils/result";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import {
  mergeWorkersById,
  type OrganizationPerson,
} from "./organizationSettings.logic";

export type OrganizationRequestError = "missing_company" | "request_failed";

export type OrganizationResult<T> = Result<T, OrganizationRequestError>;

interface AssignWorkerParams {
  readonly teamId: number;
  readonly workerId: number;
}

interface CreateTeamParams {
  readonly name: string;
  readonly unitId: number;
}

interface CreateUnitParams {
  readonly entityId: number;
  readonly name: string;
}

interface DeleteUnitParams {
  readonly entityId: number;
  readonly unitId: number;
}

interface LoadWorkersParams {
  readonly companyId: number;
  readonly teamId?: number;
}

interface UpdateEntityParams {
  readonly entityId: number;
  readonly name: string;
}

interface UpdateTeamParams {
  readonly name: string;
  readonly teamId: number;
}

interface UpdateUnitParams {
  readonly entityId: number;
  readonly name: string;
  readonly unitId: number;
}

interface OrganizationSettingsApi {
  readonly error: OrganizationRequestError | null;
  readonly isLoading: boolean;
  readonly structure: Structure;
  readonly workers: readonly OrganizationPerson[] | null;
  readonly assignWorker: (
    params: AssignWorkerParams,
  ) => Promise<OrganizationResult<void>>;
  readonly createEntity: (name: string) => Promise<OrganizationResult<void>>;
  readonly createTeam: (
    params: CreateTeamParams,
  ) => Promise<OrganizationResult<void>>;
  readonly createUnit: (
    params: CreateUnitParams,
  ) => Promise<OrganizationResult<void>>;
  readonly deleteEntity: (
    entityId: number,
  ) => Promise<OrganizationResult<void>>;
  readonly deleteTeam: (teamId: number) => Promise<OrganizationResult<void>>;
  readonly deleteUnit: (
    params: DeleteUnitParams,
  ) => Promise<OrganizationResult<void>>;
  readonly loadCompanyWorkers: () => Promise<
    OrganizationResult<OrganizationPerson[]>
  >;
  readonly loadTeamMembers: (
    teamId: number,
  ) => Promise<OrganizationResult<OrganizationPerson[]>>;
  readonly reload: () => Promise<OrganizationResult<void>>;
  readonly removeWorker: (
    params: AssignWorkerParams,
  ) => Promise<OrganizationResult<void>>;
  readonly updateEntity: (
    params: UpdateEntityParams,
  ) => Promise<OrganizationResult<void>>;
  readonly updateTeam: (
    params: UpdateTeamParams,
  ) => Promise<OrganizationResult<void>>;
  readonly updateUnit: (
    params: UpdateUnitParams,
  ) => Promise<OrganizationResult<void>>;
}

const EMPTY_STRUCTURE: Structure = { entities: [] };
const WORKERS_PAGE_SIZE = 50;

type MutationRequest = (companyId: number) => Promise<object | void>;

async function loadWorkers({
  companyId,
  teamId,
}: LoadWorkersParams): Promise<OrganizationResult<OrganizationPerson[]>> {
  try {
    let page = 0;
    let workers: OrganizationPerson[] = [];

    while (true) {
      const response = await Api().company.staff.getStaff(
        companyId,
        {
          page,
          pageSize: WORKERS_PAGE_SIZE,
          sortBy: "Worker",
          status: "All",
          workGroupFilter: teamId == null ? "All" : "InWorkGroup",
          workGroupIds: teamId == null ? undefined : [teamId],
        },
        {
          cacheMode: "network-only",
        },
      );
      const nextWorkers = response.data.items ?? [];
      workers = mergeWorkersById(workers, nextWorkers);

      if (
        (page + 1) * WORKERS_PAGE_SIZE >= response.data.total ||
        nextWorkers.length < WORKERS_PAGE_SIZE
      ) {
        return ok(workers);
      }
      page += 1;
    }
  } catch {
    return err("request_failed");
  }
}

export const useOrganizationSettings = (): OrganizationSettingsApi => {
  const [companyState] = useCompanyStore();
  const [, organizationDispatch] = useOrganizationalStructureStore();
  const companyId = companyState.company?.id;
  const [structure, setStructure] = useState<Structure>(EMPTY_STRUCTURE);
  const [workers, setWorkers] = useState<OrganizationPerson[] | null>(null);
  const [structureCompanyId, setStructureCompanyId] = useState<number | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<OrganizationRequestError | null>(null);
  const activeCompanyIdRef = useRef(companyId);
  const reloadRequestIdRef = useRef(0);
  activeCompanyIdRef.current = companyId;

  const reload = useCallback(async (): Promise<OrganizationResult<void>> => {
    if (!companyId) return err("missing_company");
    const requestId = reloadRequestIdRef.current + 1;
    reloadRequestIdRef.current = requestId;
    setIsLoading(true);
    setError(null);
    try {
      const [response, workersResult] = await Promise.all([
        Api().company.organizationalStructure.fetchCompanyStructure(companyId),
        loadWorkers({ companyId }),
      ]);
      if (
        reloadRequestIdRef.current !== requestId ||
        activeCompanyIdRef.current !== companyId
      ) {
        return err("request_failed");
      }
      setStructure(response.data);
      setStructureCompanyId(companyId);
      organizationDispatch({
        type: "GET_COMPANY_STRUCTURE",
        payload: response.data,
      });
      if (!workersResult.ok) {
        setWorkers(null);
        setError(workersResult.error);
        return err(workersResult.error);
      }
      setWorkers(workersResult.value);
      return ok(undefined);
    } catch {
      if (
        reloadRequestIdRef.current === requestId &&
        activeCompanyIdRef.current === companyId
      ) {
        setError("request_failed");
      }
      return err("request_failed");
    } finally {
      if (
        reloadRequestIdRef.current === requestId &&
        activeCompanyIdRef.current === companyId
      ) {
        setIsLoading(false);
      }
    }
  }, [companyId, organizationDispatch]);

  useEffect(() => {
    setStructure(EMPTY_STRUCTURE);
    setStructureCompanyId(null);
    setWorkers(null);
    void reload();
  }, [reload]);

  const runAndReload = useCallback(
    async (request: MutationRequest): Promise<OrganizationResult<void>> => {
      if (!companyId) return err("missing_company");
      setIsLoading(true);
      try {
        await request(companyId);
        if (activeCompanyIdRef.current !== companyId) {
          return err("request_failed");
        }
        const reloadResult = await reload();
        if (activeCompanyIdRef.current !== companyId) {
          return err("request_failed");
        }
        if (!reloadResult.ok) {
          // The mutation is already persisted; reload exposes its own UI error.
          return ok(undefined);
        }
        return reloadResult;
      } catch {
        if (activeCompanyIdRef.current === companyId) {
          setIsLoading(false);
        }
        return err("request_failed");
      }
    },
    [companyId, reload],
  );

  const createEntity = useCallback(
    (name: string): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.createEntity(
          currentCompanyId,
          name,
        ),
      );
    },
    [runAndReload],
  );

  const createUnit = useCallback(
    ({
      entityId,
      name,
    }: CreateUnitParams): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.createUnit(
          currentCompanyId,
          entityId,
          name,
        ),
      );
    },
    [runAndReload],
  );

  const createTeam = useCallback(
    ({ name, unitId }: CreateTeamParams): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.createTeam(
          currentCompanyId,
          name,
          unitId,
        ),
      );
    },
    [runAndReload],
  );

  const deleteEntity = useCallback(
    (entityId: number): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.deleteEntity({
          companyId: currentCompanyId,
          entityId,
        }),
      );
    },
    [runAndReload],
  );

  const updateUnit = useCallback(
    ({
      entityId,
      name,
      unitId,
    }: UpdateUnitParams): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.updateUnit(
          currentCompanyId,
          entityId,
          unitId,
          name,
        ),
      );
    },
    [runAndReload],
  );

  const updateEntity = useCallback(
    ({
      entityId,
      name,
    }: UpdateEntityParams): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.updateEntity({
          companyId: currentCompanyId,
          entityId,
          name,
        }),
      );
    },
    [runAndReload],
  );

  const updateTeam = useCallback(
    ({ name, teamId }: UpdateTeamParams): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.updateTeam(
          currentCompanyId,
          teamId,
          name,
        ),
      );
    },
    [runAndReload],
  );

  const deleteUnit = useCallback(
    ({
      entityId,
      unitId,
    }: DeleteUnitParams): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.deleteUnit(
          currentCompanyId,
          entityId,
          unitId,
        ),
      );
    },
    [runAndReload],
  );

  const deleteTeam = useCallback(
    (teamId: number): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.deleteTeam(
          currentCompanyId,
          teamId,
        ),
      );
    },
    [runAndReload],
  );

  const loadCompanyWorkers = useCallback((): Promise<
    OrganizationResult<OrganizationPerson[]>
  > => {
    if (!companyId) {
      return Promise.resolve(err("missing_company"));
    }
    return loadWorkers({ companyId });
  }, [companyId]);

  const loadTeamMembers = useCallback(
    (teamId: number): Promise<OrganizationResult<OrganizationPerson[]>> => {
      if (!companyId) {
        return Promise.resolve(err("missing_company"));
      }
      return loadWorkers({ companyId, teamId });
    },
    [companyId],
  );

  const assignWorker = useCallback(
    ({
      teamId,
      workerId,
    }: AssignWorkerParams): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.assignWorkerToTeam(
          currentCompanyId,
          teamId,
          workerId,
        ),
      );
    },
    [runAndReload],
  );

  const removeWorker = useCallback(
    ({
      teamId,
      workerId,
    }: AssignWorkerParams): Promise<OrganizationResult<void>> => {
      return runAndReload((currentCompanyId) =>
        Api().company.organizationalStructure.unassignWorkerFromWorkGroup(
          currentCompanyId,
          teamId,
          workerId,
        ),
      );
    },
    [runAndReload],
  );

  return {
    assignWorker,
    createEntity,
    createTeam,
    createUnit,
    deleteEntity,
    deleteTeam,
    deleteUnit,
    error,
    isLoading:
      isLoading ||
      (companyId != null && structureCompanyId !== companyId && error == null),
    loadCompanyWorkers,
    loadTeamMembers,
    reload,
    removeWorker,
    structure: structureCompanyId === companyId ? structure : EMPTY_STRUCTURE,
    updateEntity,
    updateTeam,
    updateUnit,
    workers: structureCompanyId === companyId ? workers : null,
  };
};
